import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertCircle, CreditCard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { billingClient, ACCESS_LEVELS } from '@rti/shared';

export const UpgradeBanner: React.FC = () => {
  const { user, isTrialExpired } = useAuth();
  const location = useLocation();

  if (!user || user.is_admin || location.pathname.startsWith('/billing')) {
    return null;
  }

  const tier = user.effective_membership_id ?? 0;
  if (tier >= ACCESS_LEVELS.COMPOUND) {
    return null;
  }

  if (isTrialExpired) {
    return null;
  }

  if (!user.on_free_trial) {
    return null;
  }

  const nowSec = Math.floor(Date.now() / 1000);

  let trialEndSec: number | null = null;
  if (user.free_trial_ends_at) {
    const v = Number(user.free_trial_ends_at);
    trialEndSec = v > 1e12 ? Math.floor(v / 1000) : v;
  } else if (user.free_trial_started_at && user.free_trial_duration_days) {
    trialEndSec = Number(user.free_trial_started_at) + Number(user.free_trial_duration_days) * 86400;
  }

  if (trialEndSec === null || trialEndSec <= nowSec) {
    return null;
  }

  const trialDays = Math.floor((trialEndSec - nowSec) / 86400);

  if (trialDays > 7) {
    return null;
  }

  return (
    <div className="w-full bg-rs-salmon/10 border-b border-rs-salmon px-6 py-3 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <AlertCircle className="text-rs-salmon flex-shrink-0" size={20} />
        <p className="text-sm text-rs-off-white truncate">
          {trialDays > 0 ? (
            <>
              Your free trial ends in{' '}
              <span className="font-bold text-rs-salmon">{trialDays} day{trialDays === 1 ? '' : 's'}</span>. Upgrade to keep full access.
            </>
          ) : (
            <>Your free trial ends today. Upgrade to keep full access.</>
          )}
        </p>
      </div>
      <Link
        to="/billing"
        className="inline-flex items-center gap-2 bg-rs-green hover:bg-rs-dark-green text-rs-black font-semibold px-4 py-2 rounded-lg transition-colors flex-shrink-0 text-sm force-dark-colors"
      >
        <CreditCard size={16} />
        Upgrade Now
      </Link>
    </div>
  );
};
